import { useQueryClient } from '@tanstack/react-query';
import { rewriteUserContacts, RewriteUserContactsErrorResponse } from '@uplift/api';
import { EMPTY_STRING } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { getFormErrorsFromRewriteUserContactsErrorResponse, getRewriteUserContactsInputFromFormValues } from '@uplift/utils';
import { VALIDATION_HOME_PHONE, VALIDATION_MOBILE_PHONE, VALIDATION_WORK_PHONE } from '@uplift/validations';
import { FormikHelpers } from 'formik';
import { AxiosError } from 'axios';

import * as Yup from 'yup';
import { useUpdateSettingsHandlers } from '../hooks';
import { ContactName } from '@uplift/types';

export type PhoneFormType = {
  [ContactName.mobile]: string;
  [ContactName.work]: string;
  [ContactName.home]: string;
};

export const usePhoneForm = (
  userId: string,
  contacts: Partial<PhoneFormType>,
  showWorkContact = true,
  submitCb?: (changed: boolean) => void
) => {
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const { onFormSubmitSuccess, onFormSubmitError } = useUpdateSettingsHandlers(userId, l10n.providerSettings.phone);

  const phoneFormik = () => {
    const initialValues: PhoneFormType = {
      [ContactName.mobile]: contacts[ContactName.mobile] || EMPTY_STRING,
      [ContactName.work]: contacts[ContactName.work] || EMPTY_STRING,
      [ContactName.home]: contacts[ContactName.home] || EMPTY_STRING,
    };

    const validationSchema = Yup.object({
      [ContactName.mobile]: VALIDATION_MOBILE_PHONE,
      [ContactName.work]: showWorkContact ? VALIDATION_WORK_PHONE : VALIDATION_WORK_PHONE.notRequired(),
      [ContactName.home]: VALIDATION_HOME_PHONE,
    });

    const onSubmit = async (values: PhoneFormType, formikHelpers: FormikHelpers<PhoneFormType>) => {
      try {
        await rewriteUserContacts(userId, getRewriteUserContactsInputFromFormValues(values));

        await queryClient.invalidateQueries({ queryKey: [ 'user', userId, 'contacts' ] });
        await onFormSubmitSuccess(true);

        if (submitCb) {
          submitCb(values[ContactName.mobile] !== initialValues[ContactName.mobile]);
        }
      } catch (error) {
        const errorObj = getFormErrorsFromRewriteUserContactsErrorResponse(error as AxiosError<RewriteUserContactsErrorResponse>);

        if (errorObj) {
          formikHelpers.setErrors(errorObj);
          return;
        }

        onFormSubmitError();
      }
    };

    return { initialValues, validationSchema, onSubmit };
  };

  return { phoneFormik };
};
